// For each pay week: the Monday 04:00 close, what Uber earned or is forecast to
// pay, when it lands, and that Tuesday's petrol, so each week's Uber money can be
// read against each week's petrol. Figures are cashplan_page_v1's.
import { cn } from '../../lib/shadcn/utils'
import { Chip } from '../interviews/Chip'
import { StateChip } from './StateChip'
import { AMOUNT_UNKNOWN, formatDay, formatSast, formatZar, type WeekRow } from '../../utils/cashPlan'

export function Weekly({ weeks, basis }: { weeks: WeekRow[]; basis: string | null }) {
  return (
    <div className="rounded-lg border border-border bg-card">
      <p className="border-b border-border px-4 py-2 text-xs text-muted-foreground">
        A pay week runs Monday 04:00 to Monday 04:00 and pays the Tuesday after, a business day later after a public holiday.
        The second credit lands the next business day. Forecast weeks are lighter.{basis ? ` Scheduled shifts: ${basis}.` : ''}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[56rem] text-sm">
          <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">Pay week</th>
              <th className="px-3 py-2 font-medium">Closes</th>
              <th className="px-3 py-2 font-medium">Uber</th>
              <th className="px-3 py-2 text-right font-medium">Main credit</th>
              <th className="px-3 py-2 text-right font-medium">Second credit</th>
              <th className="px-3 py-2 text-right font-medium">Tuesday petrol</th>
              <th className="px-3 py-2 text-right font-medium">Uber less petrol</th>
            </tr>
          </thead>
          <tbody>
            {weeks.map((w) => {
              const forecast = w.main_state === 'forecast'
              return (
                <tr key={w.week_start} className={cn('border-t border-border align-top', forecast && 'italic text-muted-foreground')}>
                  <td className="px-3 py-2 whitespace-nowrap not-italic text-foreground">{formatDay(w.week_start, false)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{formatSast(w.closes_at)}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1 not-italic">
                      <StateChip state={w.main_state} />
                      {w.secondary_state && w.secondary_state !== w.main_state && (
                        <StateChip state={w.secondary_state} prefix="Second" />
                      )}
                      {w.main_state === 'earned' && w.reports_complete === false && <Chip tone="amber">Reports incomplete</Chip>}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                    {w.known ? formatZar(w.main_zar) : AMOUNT_UNKNOWN}
                    <div className="text-xs text-muted-foreground">{formatDay(w.main_date)}</div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                    {formatZar(w.secondary_zar)}
                    <div className="text-xs text-muted-foreground">{formatDay(w.secondary_date)}</div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap not-italic text-foreground">
                    {w.petrol_date ? (w.petrol_known ? formatZar(w.petrol_zar) : AMOUNT_UNKNOWN) : 'None planned'}
                    {w.petrol_date && <div className="text-xs text-muted-foreground">{formatDay(w.petrol_date)}</div>}
                  </td>
                  <td className={cn('px-3 py-2 text-right tabular-nums whitespace-nowrap',
                    w.covers_petrol === false && 'font-medium text-rose-700 dark:text-rose-300')}>
                    {w.surplus_zar === null ? 'Cannot tell' : formatZar(w.surplus_zar)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
